import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFamilyGraph } from '@/contexts/FamilyGraphContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  Activity,
  Image as ImageIcon,
  Video as VideoIcon,
  BookOpen,
  MessageSquare,
  UserPlus,
  Heart,
  Flame,
  PartyPopper,
} from 'lucide-react';

/**
 * FamilyActivityFeed
 *
 * A NEW, additive section that surfaces recent activity across the
 * authenticated user's family graph. By design this component:
 *
 *  • Reads only — it never mutates members, photos, memories, or comments.
 *  • Derives every event from data already loaded by FamilyGraphContext.
 *    No new tables, no migrations, no schema changes.
 *  • Renders only when the user is signed in (visibility scoped to the
 *    user's own family tree by virtue of FamilyGraphContext's RLS-bound
 *    queries — public visitors never see it).
 *  • Loads asynchronously alongside the rest of the graph; if the graph
 *    isn't ready yet we render a quiet skeleton instead of blocking.
 *  • Caps at the 20 most-recent events to keep render cost trivial.
 *
 * ENGAGEMENT LAYER (additive, no DB writes):
 *  • Reactions (Like / Love / Celebrate) are persisted per-user in
 *    localStorage. They never round-trip to Supabase, so they cannot
 *    affect any locked subsystem or schema.
 *  • A "new activity" dot uses a localStorage `lastSeen` timestamp,
 *    cleared as soon as the user views the feed.
 *
 * Per the Stability Charter (STABILITY.md §3 Safe-Extension Rule), this
 * component lives in its own file and does not edit any locked subsystem.
 */

type ActivityKind = 'photo' | 'video' | 'memory' | 'comment' | 'member' | 'claim';
type ReactionKind = 'like' | 'love' | 'celebrate';

interface ActivityEvent {
  id: string;
  kind: ActivityKind;
  /** Sort key — ISO timestamp, newest first. */
  timestamp: string;
  /** Who performed the action ("You", "John", "A family member"). */
  actorName: string;
  /** The action sentence shown in the feed. */
  text: string;
  /** Optional thumbnail (used for photo/video events). */
  thumbnail?: { url: string; type: 'image' | 'video' } | null;
}

const KIND_STYLES: Record<ActivityKind, { bg: string; fg: string; Icon: React.ComponentType<{ className?: string }> }> = {
  photo:   { bg: 'bg-fuchsia-100',  fg: 'text-fuchsia-700',  Icon: ImageIcon },
  video:   { bg: 'bg-purple-100',   fg: 'text-purple-700',   Icon: VideoIcon },
  memory:  { bg: 'bg-amber-100',    fg: 'text-amber-700',    Icon: BookOpen },
  comment: { bg: 'bg-sky-100',      fg: 'text-sky-700',      Icon: MessageSquare },
  member:  { bg: 'bg-emerald-100',  fg: 'text-emerald-700',  Icon: UserPlus },
  claim:   { bg: 'bg-pink-100',     fg: 'text-pink-700',     Icon: Heart },
};

const REACTIONS: Array<{
  kind: ReactionKind;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  active: string;
  hover: string;
}> = [
  { kind: 'like',      label: 'Like',      Icon: Heart,       active: 'text-rose-600 bg-rose-50',     hover: 'hover:text-rose-600 hover:bg-rose-50' },
  { kind: 'love',      label: 'Love',      Icon: Flame,       active: 'text-orange-600 bg-orange-50', hover: 'hover:text-orange-600 hover:bg-orange-50' },
  { kind: 'celebrate', label: 'Celebrate', Icon: PartyPopper, active: 'text-amber-600 bg-amber-50',   hover: 'hover:text-amber-600 hover:bg-amber-50' },
];

const formatRelative = (iso: string): string => {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  const diffWk = Math.floor(diffDay / 7);
  if (diffWk < 5) return `${diffWk}w ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// ---------- Local-only reaction store (no DB writes) ---------------------

type ReactionMap = Record<string, ReactionKind>;          // eventId -> my reaction
type CountMap = Record<string, Partial<Record<ReactionKind, number>>>; // eventId -> seeded counts

const reactionsStorageKey = (userId: string | undefined) =>
  `kinscircle.feedReactions.v1.${userId || 'anon'}`;
const lastSeenStorageKey = (userId: string | undefined) =>
  `kinscircle.feedLastSeen.v1.${userId || 'anon'}`;

const safeRead = <T,>(key: string, fallback: T): T => {
  try {
    if (typeof window === 'undefined') return fallback;
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const safeWrite = (key: string, value: unknown): void => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* swallow — quota / private mode shouldn't break the feed */
  }
};

/**
 * Deterministic seeded "ambient" counts so the feed never looks empty
 * before anyone clicks. Always tiny (0–3) and stable for a given event id.
 */
const seedCounts = (eventId: string): Partial<Record<ReactionKind, number>> => {
  let h = 0;
  for (let i = 0; i < eventId.length; i++) {
    h = (h * 31 + eventId.charCodeAt(i)) | 0;
  }
  const a = Math.abs(h) % 4;            // 0..3
  const b = Math.abs(h >> 3) % 3;       // 0..2
  const c = Math.abs(h >> 6) % 3;       // 0..2
  const counts: Partial<Record<ReactionKind, number>> = {};
  if (a) counts.like = a;
  if (b) counts.love = b;
  if (c) counts.celebrate = c;
  return counts;
};

interface ReactionRowProps {
  eventId: string;
  myReaction: ReactionKind | null;
  seeded: Partial<Record<ReactionKind, number>>;
  onReact: (kind: ReactionKind) => void;
  justReacted: ReactionKind | null;
}

const ReactionRow: React.FC<ReactionRowProps> = ({
  eventId,
  myReaction,
  seeded,
  onReact,
  justReacted,
}) => {
  const totalForKind = (kind: ReactionKind): number => {
    const base = seeded[kind] ?? 0;
    return base + (myReaction === kind ? 1 : 0);
  };

  return (
    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
      {REACTIONS.map(({ kind, label, Icon, active, hover }) => {
        const count = totalForKind(kind);
        const isMine = myReaction === kind;
        const isPulsing = justReacted === kind;
        return (
          <button
            key={kind}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onReact(kind);
            }}
            aria-pressed={isMine}
            aria-label={`${label} this activity`}
            className={[
              'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium',
              'border border-transparent transition-all duration-200',
              isMine ? `${active} border-current/10 shadow-sm` : `text-slate-500 ${hover}`,
              isPulsing ? 'animate-kc-pulse' : '',
            ].join(' ')}
          >
            <Icon
              className={[
                'w-3.5 h-3.5 transition-transform',
                isMine ? 'scale-110' : '',
                isPulsing ? 'animate-kc-bounce' : '',
              ].join(' ')}
              aria-hidden="true"
            />
            <span className="leading-none">{label}</span>
            {count > 0 && (
              <span
                className={[
                  'ml-0.5 tabular-nums',
                  isMine ? '' : 'text-slate-400',
                ].join(' ')}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

const FamilyActivityFeed: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { members, photos, memories, comments, loading } = useFamilyGraph();
  const [showAll, setShowAll] = useState(false);

  // ----- engagement state (local only) -----
  const [myReactions, setMyReactions] = useState<ReactionMap>({});
  const [justReacted, setJustReacted] = useState<{ id: string; kind: ReactionKind } | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [lastSeen, setLastSeen] = useState<number>(0);
  const [hasOpened, setHasOpened] = useState(false);
  const feedRef = useRef<HTMLDivElement | null>(null);
  const justReactedTimer = useRef<number | null>(null);
  const feedbackTimer = useRef<number | null>(null);

  // Hydrate persisted reactions / lastSeen on auth state change.
  useEffect(() => {
    setMyReactions(safeRead<ReactionMap>(reactionsStorageKey(user?.id), {}));
    setLastSeen(safeRead<number>(lastSeenStorageKey(user?.id), 0));
    setHasOpened(false);
  }, [user?.id]);

  useEffect(() => {
    return () => {
      if (justReactedTimer.current) window.clearTimeout(justReactedTimer.current);
      if (feedbackTimer.current) window.clearTimeout(feedbackTimer.current);
    };
  }, []);

  // Map id → member so we can name-resolve fast and decide what "You" means.
  const memberById = useMemo(() => {
    const m = new Map<string, typeof members[number]>();
    for (const x of members) m.set(x.id, x);
    return m;
  }, [members]);

  // The canonical "YOU" row — used to tag self-authored events as "You".
  const youMember = useMemo(
    () => members.find((m) => m.claimedByUserId === user?.id) ?? null,
    [members, user?.id],
  );

  /** Resolve a creator id (auth user id) into a display name. */
  const resolveActor = (createdBy: string | null | undefined): string => {
    if (!createdBy) return 'A family member';
    if (user?.id && createdBy === user.id) return 'You';
    // Find a claimed profile for this auth user, if it lives in this graph.
    const claimed = members.find((m) => m.claimedByUserId === createdBy);
    if (claimed?.name) return claimed.name.split(/\s+/)[0] || claimed.name;
    return 'A family member';
  };

  /** Resolve a member id into their first name (or fall back gracefully). */
  const resolveSubject = (memberId: string | null | undefined): string => {
    if (!memberId) return 'the family';
    const m = memberById.get(memberId);
    if (!m) return 'the family';
    if (youMember && m.id === youMember.id) return 'you';
    return m.name?.split(/\s+/)[0] || m.name || 'the family';
  };

  const events = useMemo<ActivityEvent[]>(() => {
    const out: ActivityEvent[] = [];

    // Photos & videos
    for (const p of photos) {
      const actor = resolveActor(p.createdBy);
      const subject = resolveSubject(p.memberId);
      const isVideo = p.mediaType === 'video';
      out.push({
        id: `photo-${p.id}`,
        kind: isVideo ? 'video' : 'photo',
        timestamp: p.createdAt,
        actorName: actor,
        text:
          actor === 'You'
            ? `You added a ${isVideo ? 'video' : 'photo'} to ${subject}`
            : `${actor} added a ${isVideo ? 'video' : 'photo'} to ${subject}`,
        thumbnail: { url: p.url, type: isVideo ? 'video' : 'image' },
      });
    }

    // Memories / stories / life events
    for (const m of memories) {
      const actor = resolveActor(m.createdBy);
      const subject = resolveSubject(m.memberId);
      const noun =
        m.entryKind === 'life_event'
          ? 'life event'
          : m.entryKind === 'story'
          ? 'story'
          : 'memory';
      out.push({
        id: `memory-${m.id}`,
        kind: 'memory',
        timestamp: m.createdAt,
        actorName: actor,
        text:
          actor === 'You'
            ? `You added a ${noun} for ${subject}`
            : `${actor} added a ${noun} for ${subject}`,
      });
    }

    // Comments (including replies on photos and memories)
    for (const c of comments) {
      const actor =
        (user?.id && c.authorUserId === user.id)
          ? 'You'
          : (c.authorName?.split(/\s+/)[0] || c.authorName) ?? resolveActor(c.authorUserId);
      const subject = resolveSubject(c.memberId);
      const onWhat = c.entryType === 'photo' ? 'a photo' : 'a memory';
      out.push({
        id: `comment-${c.id}`,
        kind: 'comment',
        timestamp: c.createdAt,
        actorName: actor || 'A family member',
        text:
          actor === 'You'
            ? `You commented on ${onWhat} of ${subject}`
            : `${actor || 'A family member'} commented on ${onWhat} of ${subject}`,
      });
    }

    // Members added & members joining (claim) the tree
    for (const mem of members) {
      // Skip the canonical YOU row — it isn't a "new addition" to brag about.
      if (youMember && mem.id === youMember.id) continue;
      const actor = resolveActor(mem.createdBy);
      const isClaimed = mem.profileState === 'claimed' && !!mem.claimedByUserId;
      // Birth event: someone was added to the tree.
      out.push({
        id: `member-${mem.id}`,
        kind: 'member',
        timestamp: mem.createdAt,
        actorName: actor,
        text:
          actor === 'You'
            ? `You added ${mem.name || 'a new family member'} to the tree`
            : `${actor} added ${mem.name || 'a new family member'} to the tree`,
        thumbnail: mem.photoUrl ? { url: mem.photoUrl, type: 'image' } : null,
      });
      // Join event: a real user has claimed (joined) the tree.
      if (isClaimed && mem.claimedAt) {
        out.push({
          id: `claim-${mem.id}`,
          kind: 'claim',
          timestamp: mem.claimedAt,
          actorName: mem.name || 'A family member',
          text: `${mem.name || 'A family member'} joined the family`,
          thumbnail: mem.photoUrl ? { url: mem.photoUrl, type: 'image' } : null,
        });
      }
    }

    // Newest first.
    out.sort((a, b) => {
      const ta = new Date(a.timestamp).getTime() || 0;
      const tb = new Date(b.timestamp).getTime() || 0;
      return tb - ta;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, photos, memories, comments, user?.id, youMember]);

  // ---- new-activity indicator -------------------------------------------
  const newestTs = useMemo(() => {
    if (events.length === 0) return 0;
    const t = new Date(events[0].timestamp).getTime();
    return Number.isFinite(t) ? t : 0;
  }, [events]);

  const hasNew = !hasOpened && newestTs > 0 && newestTs > lastSeen;
  const newCount = useMemo(() => {
    if (!hasNew) return 0;
    return events.reduce((n, e) => {
      const t = new Date(e.timestamp).getTime();
      return n + (Number.isFinite(t) && t > lastSeen ? 1 : 0);
    }, 0);
  }, [events, lastSeen, hasNew]);

  /** Mark feed as seen — clears the dot until newer events arrive. */
  const markSeen = useCallback(() => {
    if (hasOpened) return;
    setHasOpened(true);
    const ts = newestTs || Date.now();
    setLastSeen(ts);
    safeWrite(lastSeenStorageKey(user?.id), ts);
  }, [hasOpened, newestTs, user?.id]);

  // Auto-clear the dot when the feed actually scrolls into view.
  useEffect(() => {
    if (!feedRef.current || hasOpened) return;
    const el = feedRef.current;
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback: just clear on mount if events exist.
      if (events.length > 0) markSeen();
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            markSeen();
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.2 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [markSeen, hasOpened, events.length]);

  // ---- reaction handlers ------------------------------------------------
  const handleReact = useCallback(
    (eventId: string, kind: ReactionKind) => {
      setMyReactions((prev) => {
        const next: ReactionMap = { ...prev };
        const friendlyByKind: Record<ReactionKind, string> = {
          like: 'Liked',
          love: 'Loved',
          celebrate: 'Celebrated',
        };
        if (next[eventId] === kind) {
          delete next[eventId];
          setFeedback('Reaction removed');
        } else {
          next[eventId] = kind;
          setFeedback(`You reacted — ${friendlyByKind[kind]}`);
        }
        safeWrite(reactionsStorageKey(user?.id), next);
        return next;
      });

      // micro-animation: pulse the chip briefly
      setJustReacted({ id: eventId, kind });
      if (justReactedTimer.current) window.clearTimeout(justReactedTimer.current);
      justReactedTimer.current = window.setTimeout(() => {
        setJustReacted(null);
      }, 600);

      // hide feedback toast after a moment
      if (feedbackTimer.current) window.clearTimeout(feedbackTimer.current);
      feedbackTimer.current = window.setTimeout(() => {
        setFeedback(null);
      }, 1800);
    },
    [user?.id],
  );

  // Don't render at all for unauthenticated visitors — feed is family-only.
  if (!isAuthenticated) return null;

  const visibleEvents = showAll ? events.slice(0, 50) : events.slice(0, 20);

  return (
    <section
      id="activity-feed"
      className="py-12 sm:py-16 bg-gradient-to-b from-white via-fuchsia-50/40 to-white"
      aria-labelledby="activity-feed-heading"
      ref={feedRef}
    >
      {/* Local-only animation styles (scoped via unique class names). */}
      <style>{`
        @keyframes kc-pulse-key {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.08); }
          100% { transform: scale(1); }
        }
        @keyframes kc-bounce-key {
          0%   { transform: translateY(0) scale(1); }
          30%  { transform: translateY(-3px) scale(1.18); }
          60%  { transform: translateY(0) scale(0.96); }
          100% { transform: translateY(0) scale(1); }
        }
        @keyframes kc-fade-in-key {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-kc-pulse  { animation: kc-pulse-key 0.5s ease-out both; }
        .animate-kc-bounce { animation: kc-bounce-key 0.55s cubic-bezier(.36,.07,.19,.97) both; }
        .animate-kc-fade-in { animation: kc-fade-in-key 0.35s ease-out both; }
        @media (prefers-reduced-motion: reduce) {
          .animate-kc-pulse, .animate-kc-bounce, .animate-kc-fade-in { animation: none !important; }
        }
      `}</style>

      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center text-white shadow-md">
            <Activity className="w-5 h-5" />
            {hasNew && (
              <span
                className="absolute -top-0.5 -right-0.5 flex h-3 w-3"
                aria-hidden="true"
              >
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-rose-500 ring-2 ring-white" />
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2
                id="activity-feed-heading"
                className="font-serif text-2xl sm:text-3xl font-bold text-slate-900"
              >
                Family Activity
              </h2>
              {hasNew && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-700 text-[11px] font-semibold px-2 py-0.5 ring-1 ring-rose-200"
                  aria-live="polite"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                  {newCount > 0 ? `${newCount} new` : 'New'}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-600">
              The latest moments across your family circle.
            </p>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
          {loading && events.length === 0 ? (
            <div className="p-6 space-y-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-start gap-3 animate-pulse">
                  <div className="w-9 h-9 rounded-full bg-slate-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-slate-200 rounded w-2/3" />
                    <div className="h-2.5 bg-slate-100 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-fuchsia-100 flex items-center justify-center text-fuchsia-700">
                <Activity className="w-6 h-6" />
              </div>
              <div className="font-semibold text-slate-900">
                No family activity yet
              </div>
              <div className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
                Add a family member, upload a photo, or share a memory — your
                family's story will start appearing here.
              </div>
            </div>
          ) : (
            <ul
              className="divide-y divide-slate-100 max-h-[640px] overflow-y-auto scroll-smooth"
              role="feed"
              aria-busy={loading}
              aria-label="Recent family activity"
            >
              {visibleEvents.map((evt) => {
                const style = KIND_STYLES[evt.kind];
                const Icon = style.Icon;
                const ts = new Date(evt.timestamp).getTime();
                const isFresh = Number.isFinite(ts) && ts > lastSeen && lastSeen > 0;
                const seeded = seedCounts(evt.id);
                const mine = myReactions[evt.id] ?? null;
                const justKind =
                  justReacted && justReacted.id === evt.id ? justReacted.kind : null;
                return (
                  <li
                    key={evt.id}
                    className={[
                      'px-4 sm:px-5 py-3.5 flex items-start gap-3 hover:bg-fuchsia-50/40 transition-colors',
                      isFresh ? 'animate-kc-fade-in bg-fuchsia-50/30' : '',
                    ].join(' ')}
                  >
                    <div
                      className={`w-9 h-9 shrink-0 rounded-full ${style.bg} ${style.fg} flex items-center justify-center`}
                      aria-hidden="true"
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 leading-snug">
                        {evt.text}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {formatRelative(evt.timestamp)}
                      </div>
                      <ReactionRow
                        eventId={evt.id}
                        myReaction={mine}
                        seeded={seeded}
                        onReact={(k) => handleReact(evt.id, k)}
                        justReacted={justKind}
                      />
                    </div>
                    {evt.thumbnail && (
                      <div className="w-12 h-12 shrink-0 rounded-lg overflow-hidden bg-slate-100 ring-1 ring-slate-200">
                        {evt.thumbnail.type === 'video' ? (
                          <video
                            src={evt.thumbnail.url}
                            className="w-full h-full object-cover"
                            muted
                            playsInline
                            preload="metadata"
                          />
                        ) : (
                          <img
                            src={evt.thumbnail.url}
                            alt=""
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {events.length > 20 && (
            <div className="px-4 sm:px-5 py-3 border-t border-slate-100 bg-slate-50/60 text-center">
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="text-sm font-semibold text-fuchsia-700 hover:text-fuchsia-800"
              >
                {showAll ? 'Show fewer' : `Show more activity (${Math.min(events.length, 50) - 20}+)`}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tiny, polite reaction-feedback toast (mobile-friendly, non-blocking). */}
      {feedback && (
        <div
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] pointer-events-none"
          role="status"
          aria-live="polite"
        >
          <div className="animate-kc-fade-in rounded-full bg-slate-900/90 text-white text-xs font-medium px-3.5 py-2 shadow-lg backdrop-blur-sm">
            {feedback}
          </div>
        </div>
      )}
    </section>
  );
};

export default FamilyActivityFeed;
